import { GitBranch, Layers3, Network, TimerReset } from 'lucide-react'
import type { Document } from '@agentwiki/shared'
import { DocumentStatusBadge } from '../shared/document-status-badge'
import {
  formatCompileMode,
  formatMemoryClass,
  formatRelativeSyncTime,
  formatSourcePath,
  formatSourceType,
  getDocumentKindInfo,
  getDocumentOwnershipInfo,
} from '../../lib/document-kind'
import { formatSourceRevision, getDocumentSourceIdentity } from '../../lib/document-source-identity'
import { cn } from '../../lib/utils'

export function DocumentProvenanceBanner({ doc }: { doc: Document }) {
  const source = doc.sourceProvenance
  const info = getDocumentKindInfo(doc)
  const ownership = getDocumentOwnershipInfo(doc)
  const sourcePath = formatSourcePath(source?.sourcePath, 72)
  const identity = source ? getDocumentSourceIdentity(source) : null

  const rows = source
    ? [
        {
          icon: GitBranch,
          label: identity?.kindLabel ?? 'Source',
          value: identity?.label ?? formatSourceType(source.sourceType),
        },
        {
          icon: GitBranch,
          label: 'Source path',
          value: sourcePath,
        },
        {
          icon: GitBranch,
          label: 'Revision',
          value: formatSourceRevision(source),
        },
        {
          icon: Layers3,
          label: 'Compile mode',
          value: formatCompileMode(source.compileMode),
        },
        {
          icon: Network,
          label: 'Memory class',
          value: formatMemoryClass(source.memoryClass),
        },
        {
          icon: TimerReset,
          label: 'Last synced',
          value: formatRelativeSyncTime(source.lastSyncedAt),
        },
      ]
    : [
        {
          icon: GitBranch,
          label: 'Source',
          value: 'AgentWiki workspace',
        },
        {
          icon: Layers3,
          label: 'Compile mode',
          value: 'Not compiled',
        },
        {
          icon: Network,
          label: 'Memory class',
          value: 'Not in typed memory',
        },
        {
          icon: TimerReset,
          label: 'Last synced',
          value: 'Not synced',
        },
      ]

  return (
    <section
      className={cn('aw-doc-provenance', `is-${info.kind}`)}
      aria-label="Document provenance"
    >
      <details className="aw-doc-provenance-details">
        <summary className="aw-doc-provenance-summary">
          <span className="aw-doc-provenance-lead">
            <span className="aw-doc-ownership-label">{ownership.label}</span>
            {info.kind !== 'verified' && <DocumentStatusBadge doc={doc} />}
            <span>{info.description}</span>
            {source?.sourceType && <span>{formatSourceType(source.sourceType)}</span>}
            {source?.sourcePath && (
              <span className="aw-doc-provenance-source">
                {formatSourcePath(source.sourcePath, 52)}
              </span>
            )}
          </span>
          <span className="aw-doc-provenance-toggle">Details</span>
        </summary>

        <div className="aw-doc-provenance-grid">
          {rows.map((row) => {
            const Icon = row.icon
            return (
              <div key={row.label} className="aw-doc-provenance-item">
                <Icon className="h-3.5 w-3.5" />
                <span>{row.label}</span>
                <strong title={row.value}>{row.value}</strong>
              </div>
            )
          })}
        </div>
      </details>
    </section>
  )
}
